import { useDeferredValue, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { DeleteConfirmDialog } from '~/components/common/DeleteConfirmDialog'
import {
  CollapsibleFilterToolbar,
  FilterGrid,
} from '~/components/common/filters'
import {
  type BusinessMasterRow,
  convertListBusinessMastersResponse,
  useListBusinessMasters,
} from '~/hooks/businessMaster/useListBusinessMasters'
import { MasterCrudTabShell } from '~/pages/AdminOcctoMaster/shell/MasterCrudTabShell'
import { useMasterCrudTab } from '~/pages/AdminOcctoMaster/shell/useMasterCrudTab'
import { useMasterInlineDelete } from '~/pages/AdminOcctoMaster/shell/useMasterInlineDelete'
import { BusinessMasterTable } from '~/pages/AdminOcctoMaster/tabs/BusinessMaster/components/BusinessMasterTable'
import { CreateBusinessMasterModal } from '~/pages/AdminOcctoMaster/tabs/BusinessMaster/components/CreateBusinessMasterModal'
import { EditBusinessMasterModal } from '~/pages/AdminOcctoMaster/tabs/BusinessMaster/components/EditBusinessMasterModal'
import { useDeleteBusinessMaster } from '~/pages/AdminOcctoMaster/tabs/BusinessMaster/hooks/useDeleteBusinessMaster'
import { businessMasterCsvColumns } from '~/pages/AdminOcctoMaster/tabs/BusinessMaster/models/labels'
import { ExpiredFilterToggle } from '~/pages/AdminOcctoMaster/tabs/_common/components/ExpiredFilterToggle'
import { OCCTO_MASTER_FILTER_STORAGE_KEY } from '~/pages/AdminOcctoMaster/tabs/_common/components/MasterFilterToolbar'
import { SearchInput } from '~/pages/AdminOcctoMaster/tabs/_common/components/SearchInput'

function filterRows(
  rows: BusinessMasterRow[],
  searchText: string,
): BusinessMasterRow[] {
  const trimmed = searchText.trim().toLowerCase()
  const filtered = rows.filter((row) => {
    if (trimmed === '') {
      return true
    }
    return (
      row.businessCode.toLowerCase().includes(trimmed) ||
      row.businessName.toLowerCase().includes(trimmed)
    )
  })
  return filtered
    .slice()
    .sort((a, b) => a.businessCode.localeCompare(b.businessCode))
}

export const BusinessMasterTab = (): React.ReactElement => {
  const { t } = useTranslation()
  const { data, isLoading, isError, error } = useListBusinessMasters()

  const [searchText, setSearchText] = useState('')
  const deferredSearchText = useDeferredValue(searchText)

  const rows = useMemo(
    () => (data !== undefined ? convertListBusinessMastersResponse(data) : []),
    [data],
  )
  const filteredRows = useMemo(
    () => filterRows(rows, deferredSearchText),
    [rows, deferredSearchText],
  )

  const subject = t('page:adminOcctoMaster.common.subject.business')
  const searchLabel = t('page:adminOcctoMaster.toolbar.searchLabel')
  const tab = useMasterCrudTab<BusinessMasterRow>({
    data,
    isLoading,
    isError,
    error,
    errorKeyPrefix: 'page:adminOcctoMaster.businessMaster.serverError',
    filteredRows,
    csv: { columns: businessMasterCsvColumns(), fileBase: 'business' },
    expiry: {
      getPeriod: (row) => ({
        validFrom: row.validFrom,
        validTo: row.validTo,
      }),
      subject,
    },
  })
  const { handleDeleteConfirm, isDeleting } =
    useMasterInlineDelete<BusinessMasterRow>({
      modalState: tab.modalState,
      closeModal: tab.closeModal,
      setDeleteError: tab.setDeleteError,
      errorKeyPrefix: 'page:adminOcctoMaster.businessMaster.serverError',
      useDelete: useDeleteBusinessMaster,
    })

  const deleteRow = tab.modalState.kind === 'delete' ? tab.modalState.row : null

  return (
    <MasterCrudTabShell
      createModal={
        <CreateBusinessMasterModal
          isOpen={tab.modalState.kind === 'create'}
          onClose={tab.closeModal}
        />
      }
      csvExportDisabledReason={tab.csvExportDisabledReason}
      deleteDialog={
        deleteRow !== null ? (
          <DeleteConfirmDialog
            entityName={t(
              'page:adminOcctoMaster.businessMaster.deleteEntityName',
              {
                code: deleteRow.businessCode,
                name: deleteRow.businessName,
              },
            )}
            errorIsRetriable={tab.deleteError?.retriable}
            errorMessage={tab.deleteError?.message}
            isLoading={isDeleting}
            isOpen={tab.modalState.kind === 'delete'}
            onCancel={tab.closeModal}
            onConfirm={() => {
              void handleDeleteConfirm()
            }}
            title={t(
              'page:adminOcctoMaster.businessMaster.delete.confirmTitle',
            )}
          />
        ) : null
      }
      editModal={
        tab.modalState.kind === 'edit' ? (
          <EditBusinessMasterModal
            row={tab.modalState.row}
            isOpen
            onClose={tab.closeModal}
          />
        ) : null
      }
      filterToolbar={
        // 事業者マスタはエリア絞り込みを持たないため MasterFilterToolbar を使わず
        // 検索 + 失効トグルの 2 slot だけを組む (開閉の保存キー・件数・クリアの決め方は
        // MasterFilterToolbar と同じ。 検索しか無い画面なので、 要約が空にならないよう検索は常に出す)。
        <CollapsibleFilterToolbar
          modifiedCount={
            [searchText !== '', tab.includeExpired].filter(Boolean).length
          }
          onClear={() => {
            setSearchText('')
            tab.setIncludeExpired(false)
          }}
          storageKey={OCCTO_MASTER_FILTER_STORAGE_KEY}
          summary={[
            {
              key: 'search',
              label: searchLabel,
              value: searchText === '' ? '—' : searchText,
            },
            {
              key: 'expired',
              label: t('page:adminOcctoMaster.common.includeExpired', {
                subject,
              }),
              value: tab.includeExpired
                ? t('page:adminOcctoMaster.common.valueYes')
                : t('page:adminOcctoMaster.common.valueNo'),
            },
          ]}
        >
          <FilterGrid
            items={[
              {
                key: 'search',
                label: searchLabel,
                control: (
                  <SearchInput
                    onChange={setSearchText}
                    placeholder={t(
                      'page:adminOcctoMaster.businessMaster.toolbar.searchPlaceholder',
                    )}
                    value={searchText}
                  />
                ),
              },
              {
                key: 'expired',
                control: (
                  <ExpiredFilterToggle
                    expiredCount={tab.expiredCount}
                    includeExpired={tab.includeExpired}
                    onChange={tab.setIncludeExpired}
                    subject={subject}
                  />
                ),
              },
            ]}
          />
        </CollapsibleFilterToolbar>
      }
      onAddIndividual={tab.openCreate}
      onCsvExport={tab.csvExportEnabled ? tab.handleCsvExport : undefined}
      showInitialLoading={tab.showInitialLoading}
      table={
        <BusinessMasterTable
          emptyLabel={tab.emptyLabel}
          errorLabel={tab.errorLabel}
          isLoading={isLoading}
          onDelete={tab.openDelete}
          onEdit={tab.openEdit}
          rows={tab.visibleRows}
          today={tab.today}
        />
      }
    />
  )
}
